import React, { useEffect, useState, useRef } from 'react';
import { getPageBySlug } from '../../utils/wordpressApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Services.css';
import { Link } from 'react-router-dom';

// Import ikon
import thunderIcon from '../../assets/images/icons/thunder.png';
import internetIcon from '../../assets/images/icons/internet.png';
import gndIcon from '../../assets/images/icons/gnd.png';
import cctvIcon from '../../assets/images/icons/cctv.png';
import fireIcon from '../../assets/images/icons/fire.png';
import bellIcon from '../../assets/images/icons/bell.png';
import measurementIcon from '../../assets/images/icons/measurement.png';
import homeIcon from '../../assets/images/icons/home.png';

const Offer = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parsedData, setParsedData] = useState({
    intro: '',
    services: []
  });

  // Refs dla animacji
  const headerRef = useRef(null);
  const servicesRef = useRef(null);
  const processRef = useRef(null);
  const ctaRef = useRef(null);

  // Fallback data matching WordPress content exactly
  const defaultIntro = 'Oferujemy kompleksowe rozwiązania elektryczne i teletechniczne dostosowane do indywidualnych potrzeb każdego klienta. Nasze usługi charakteryzują się wysoką jakością wykonania, nowoczesnymi technologiami oraz konkurencyjnymi cenami.';
  
  const defaultServices = [
    {
      title: 'Instalacje elektryczne',
      description: 'Kompleksowe instalacje elektryczne dla domów, biur i obiektów przemysłowych. Realizujemy projekty od podstaw, dbając o bezpieczeństwo i funkcjonalność każdej instalacji.',
      icon: thunderIcon,
      features: []
    },
    {
      title: 'Sieci LAN',
      description: 'Profesjonalne wykonanie i certyfikacja sieci komputerowych. Specjalizujemy się w infrastrukturze pasywnej z gwarancjami 25 i 30-letnimi. Posiadamy certyfikaty uprawniające do budowania sieci LAN z najwyższymi standardami jakości.',
      icon: internetIcon,
      features: []
    },
    {
      title: 'Instalacje odgromowe i uziemiające',
      description: 'Wykonujemy instalacje odgromowe i uziemiające dla budynków wielorodzinnych, obiektów użyteczności publicznej, magazynów oraz hal przemysłowych. Realizujemy również połączenia wyrównawcze wewnątrz budynków zgodnie z normami bezpieczeństwa.',
      icon: gndIcon,
      features: []
    },
    {
      title: 'Monitoring CCTV',
      description: 'Profesjonalne systemy monitoringu wizyjnego z możliwością zdalnego dostępu przez aplikacje mobilne. Korzystamy z najnowszych technologii Full HD i 4K.',
      icon: cctvIcon,
      features: []
    },
    {
      title: 'Systemy przeciwpożarowe aktywne i pasywne',
      description: 'Kompleksowe systemy bezpieczeństwa pożarowego obejmujące instalacje sygnalizacji pożarowej (SSP), systemy oddymiające oraz pasywne przejścia przeciwpożarowe. Dbamy o pełne zabezpieczenie obiektów zgodnie z przepisami przeciwpożarowymi.',
      icon: fireIcon,
      features: []
    },
    {
      title: 'Systemy alarmowe',
      description: 'Nowoczesne systemy zabezpieczeń chroniące Twój dom i firmę przed włamaniem i kradzieżą. Oferujemy rozwiązania dostosowane do specyfiki każdego obiektu z możliwością integracji z systemami monitoringu.',
      icon: bellIcon,
      features: []
    },
    {
      title: 'Pomiary elektryczne i teletechniczne',
      description: 'Wykonujemy profesjonalne pomiary w zakresie certyfikacji sieci LAN, pomiary natężenia oświetlenia oraz kompleksowe pomiary elektryczne. Posiadamy nowoczesne przyrządy pomiarowe i odpowiednie kwalifikacje do przeprowadzania certyfikowanych pomiarów.',
      icon: measurementIcon,
      features: []
    },
    {
      title: 'Automatyka budynkowa',
      description: 'Inteligentne systemy zarządzania budynkiem i technologie Smart Home, które zwiększają komfort życia i pozwalają oszczędzać energię. Oferujemy kompleksowe rozwiązania automatyki dostosowane do indywidualnych potrzeb klientów.',
      icon: homeIcon,
      features: []
    }
  ];

  useEffect(() => {
    const fetchOfferPage = async () => {
      try {
        setLoading(true);
        setError(null);
        const offerPage = await getPageBySlug('oferta');
        setPage(offerPage);
        
        if (offerPage?.content?.rendered) {
          parseWordPressContent(offerPage.content.rendered);
        } else {
          // Jeśli nie ma danych z WP, użyj fallback
          setParsedData({
            intro: defaultIntro,
            services: defaultServices
          });
        }
      } catch (err) {
        console.warn('WordPress not available, using fallback data:', err);
        // Nie ustawiaj błędu, tylko użyj fallback danych
        setParsedData({
          intro: defaultIntro,
          services: defaultServices
        });
        setPage({
          title: { rendered: 'Nasza Oferta' }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOfferPage();
  }, []);

  // Hook dla animacji po załadowaniu
  useEffect(() => {
    if (!loading && !error) {
      // Małe opóźnienie aby zapewnić że DOM jest gotowy
      const timer = setTimeout(() => {
        animateElements();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [loading, error]);

  // Funkcja animująca elementy
  const animateElements = () => {
    // Animacja nagłówka
    if (headerRef.current) {
      const headerContent = headerRef.current.querySelector('.header-content');
      if (headerContent) {
        headerContent.classList.add('animated');
      }
    }

    // Animacja kart usług z opóźnieniem
    if (servicesRef.current) {
      const serviceCards = servicesRef.current.querySelectorAll('.service-card');
      serviceCards.forEach((card, index) => {
        setTimeout(() => {
          card.classList.add('animated');
        }, index * 150);
      });
    }

    // Animacja tytułu sekcji procesu
    if (processRef.current) {
      const sectionTitle = processRef.current.querySelector('.section-title');
      if (sectionTitle) {
        setTimeout(() => {
          sectionTitle.classList.add('animated');
        }, 300);
      }

      // Animacja kroków procesu
      const processSteps = processRef.current.querySelectorAll('.process-step');
      processSteps.forEach((step, index) => {
        setTimeout(() => {
          step.classList.add('animated');
        }, 500 + (index * 150));
      });
    }

    // Animacja sekcji CTA
    if (ctaRef.current) {
      const ctaContent = ctaRef.current.querySelector('.cta-content');
      if (ctaContent) {
        setTimeout(() => {
          ctaContent.classList.add('animated');
        }, 800);
      }
    }
  };

  // Mapowanie nazw usług na ikony
  const getIconForService = (title) => {
    const iconMap = {
      'Instalacje elektryczne': thunderIcon,
      'Sieci LAN': internetIcon,
      'Instalacje odgromowe i uziemiające': gndIcon,
      'Monitoring CCTV': cctvIcon,
      'Systemy przeciwpożarowe aktywne i pasywne': fireIcon,
      'Systemy alarmowe': bellIcon,
      'Pomiary elektryczne i teletechniczne': measurementIcon,
      'Automatyka budynkowa': homeIcon
    };
    
    return iconMap[title] || thunderIcon; // fallback do thunder.png
  };

  const parseWordPressContent = (htmlContent) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
    const headings = Array.from(tempDiv.querySelectorAll('h2, h3'));
    
    // Wyciągamy intro (pierwszy paragraf)
    const intro = paragraphs[0]?.textContent || defaultIntro;
    
    // Parsujemy usługi na podstawie nagłówków
    const services = [];
    
    headings.forEach((heading) => {
      const title = heading.textContent;
      let currentElement = heading.nextElementSibling;
      let description = '';
      
      // Sprawdzamy kolejne elementy po nagłówku
      while (currentElement) {
        if (currentElement.tagName === 'P') {
          const textContent = currentElement.textContent.trim();
          
          // Sprawdzamy czy paragraf zawiera tylko emotkę
          const isEmojiOnly = /^[\u{1F300}-\u{1F9FF}][\u{FE00}-\u{FE0F}]?$|^[\u{2600}-\u{26FF}][\u{FE00}-\u{FE0F}]?$|^[\u{2700}-\u{27BF}][\u{FE00}-\u{FE0F}]?$/u.test(textContent);
          
          if (!isEmojiOnly && textContent && !description) {
            // To jest opis - zapisujemy go jako description
            description = textContent;
          }
        } else if (currentElement.tagName === 'H2' || currentElement.tagName === 'H3') {
          // Jeśli trafimy na kolejny nagłówek, przerywamy
          break;
        }
        
        currentElement = currentElement.nextElementSibling;
      }
      
      if (title && description) {
        services.push({
          title,
          description,
          icon: getIconForService(title),
          features: []
        });
      }
    });
    
    setParsedData({
      intro,
      services: services.length > 0 ? services : defaultServices
    });
  };

  // Funkcja do określenia klasy CSS dla siatki w zależności od liczby elementów
  const getGridClass = (totalServices) => {
    // Użyj flexbox dla lepszej kontroli centrowania
    return 'services-grid-flex';
  };

  // Zawsze używaj danych - z WP lub fallback
  const introToDisplay = parsedData.intro || defaultIntro;
  const servicesToDisplay = parsedData.services.length > 0 ? parsedData.services : defaultServices;
  const titleToDisplay = page?.title?.rendered || 'Nasza Oferta';

  if (loading) {
    return <LoadingSpinner text="Ładowanie oferty..." />;
  }

  return (
    <div className="offer-page">
      {/* Page Header */}
      <section className="page-header" ref={headerRef}>
        <div className="container">
          <div className="header-content">
            <h1>{titleToDisplay}</h1>
            <p>{introToDisplay}</p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-section section" ref={servicesRef}>
        <div className="container">
          <div className={getGridClass(servicesToDisplay.length)}>
            {servicesToDisplay.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-icon">
                  <img src={service.icon} alt={service.title} className="service-icon-img" />
                </div>
                <h3>{service.title}</h3>
                <p className="service-description">{service.description}</p>
                {service.features && service.features.length > 0 && (
                  <ul className="service-features">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex}>{feature}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section" ref={processRef}>
        <div className="container">
          <h2 className="section-title">Jak pracujemy?</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Konsultacja</h3>
              <p>Bezpłatne spotkanie i analiza potrzeb</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Projekt</h3>
              <p>Przygotowanie dokumentacji technicznej</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Realizacja</h3>
              <p>Profesjonalne wykonanie zgodnie z normami</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Dokumentacja powykonawcza</h3>
              <p>Przekazanie kompletnej dokumentacji technicznej</p>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <h3>Serwis i utrzymanie</h3>
              <p>Długoterminowe wsparcie i gwarancja</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section" ref={ctaRef}>
        <div className="container">
          <div className="cta-content">
            <h2>Skontaktuj się z nami</h2>
            <p>Otrzymaj bezpłatną wycenę i profesjonalne doradztwo</p>
            <div className="cta-buttons">
              <Link className="btn btn-primary btn-large" to="/kontakt">
                Zadzwoń teraz
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Offer;